import { Injectable, TemplateRef, inject } from '@angular/core';
import { DvCheckboxElement, DvDynamicFormElement } from '@devinco/ui/legacy';
import { TranslateService } from '@ngx-translate/core';
import { TimedMarkerMapMenuPanelComponent } from '@shared/components/timed-marker-map-menu-panel/timed-marker-map-menu-panel.component';
import { FullScreenMapComponent } from '@features/map/full-screen-map/full-screen-map.component';
import {
  AutoZoomFitParams,
  FullScreenMapProps,
  MapMarker,
  MapInput,
} from '@features/map/interfaces';
import MarkerPopupUtils from '@features/map/utils/marker-popup.utils';
import DateUtils from '@shared/utils/date.utils';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  CustomVehicleMapSettings,
  VehicleDto,
} from '@pages/vehicles-page/components/vehicles-grid/interfaces';
import { DialogService } from '@core/services/dialog.service';
import { DvDialogComponent } from '@devinco/ui/dds';
import { MapId } from '@features/map/constants/map-id.enum';
import TimedMapMarkerUtils from '@shared/components/timed-marker-map-menu-panel/timed-marker.utils';
import RouteNetworkColorUtils from '@pages/route-network-page/utils/route-network-color.utils';

@Injectable()
export class VehiclesMapService {
  private dialog = inject(DialogService);
  private translate = inject(TranslateService);

  customVehicleSettingElements: DvDynamicFormElement[] = [
    new DvCheckboxElement({
      colspan: 12,
      key: 'RegistrationNumber',
      checkboxLabel: 'VehicleMapSettings.RegistrationNumber',
      rowspan: 2,
      extra: { defaultValue: true },
    }),
    new DvCheckboxElement({
      colspan: 12,
      key: 'UserDescription',
      checkboxLabel: 'VehicleMapSettings.UserDescription',
      rowspan: 2,
      extra: { defaultValue: true },
    }),
    new DvCheckboxElement({
      colspan: 12,
      key: 'VehicleDescription',
      checkboxLabel: 'VehicleMapSettings.VehicleDescription',
      rowspan: 2,
    }),
    new DvCheckboxElement({
      colspan: 12,
      key: 'Comment',
      checkboxLabel: 'VehicleMapSettings.Comment',
      rowspan: 2,
    }),
  ];

  showVehiclesOnMap(
    mapId: MapId,
    markers$: Observable<MapInput[]>,
    menuPanelTemplate: TemplateRef<TimedMarkerMapMenuPanelComponent>,
    autoZoomFit$?: BehaviorSubject<AutoZoomFitParams>,
    toggleMarkerClustering$?: BehaviorSubject<boolean>
  ): DvDialogComponent<FullScreenMapProps, FullScreenMapComponent> {
    const data: FullScreenMapProps = {
      id: mapId,
      markers$: markers$,
      autoZoomFit$,
      toggleMarkerClustering$,
      fitBoundsAfterRefresh: false,
      useMarkerClustering: true,
      customTemplate: {
        template: menuPanelTemplate,
        isFullScreenTemplate: true,
      },
    };

    return this.dialog.open<FullScreenMapProps, FullScreenMapComponent>({
      size: 'full',
      contentComponent: FullScreenMapComponent,
      hideHeader: true,
      hideFooter: true,
      data,
    });
  }

  getMapMarkers(
    vehicles: VehicleDto[],
    customSettingsFormValue?: CustomVehicleMapSettings
  ): MapInput[] {
    const points: MapMarker[] = vehicles
      .filter(vehicle => vehicle.LastGPSLat && vehicle.LastGPSLon)
      .map(vehicle => {
        let vehicleIconUrl: string;
        let iconSize = 40;
        const timeDifference = DateUtils.getTimeDifferenceInMinutes(
          vehicle.LastActivityTime!,
          DateUtils.getCurrentNOTime()
        );
        if (vehicle.IsRouteNetworkMaintainer) {
          vehicleIconUrl =
            RouteNetworkColorUtils.getRouteMaintainerColorAndIconUrl(
              vehicle.RouteNetworkMaintainerTypeID,
              vehicle.LastActivityTime!
            ).iconUrl;
          iconSize = 56;
        } else {
          vehicleIconUrl = TimedMapMarkerUtils.getEntityIconByTimeDifference(
            timeDifference,
            'Vehicle'
          );
        }
        return {
          lat: vehicle.LastGPSLat!,
          lng: vehicle.LastGPSLon!,
          options: {
            riseOnHover: true,
            tooltipValue: this.getTooltipFromMapSettings(
              vehicle,
              customSettingsFormValue
            ),
            isToolTipPermanent: true,
            rotationAngle: vehicle.LastCourse,
            customMarker: {
              iconUrl: vehicleIconUrl,
              iconSize: [iconSize, iconSize],
            },
            popup: {
              customElement:
                MarkerPopupUtils.getVehiclePopupElementFromRegistry(
                  vehicle,
                  vehicleIconUrl,
                  true
                ),
              popupTableData: {
                iconHeader: this.translate.instant('General.Icon'),
                contentData: this.constructClusterPopupTableData(
                  vehicle,
                  vehicleIconUrl
                ),
              },
            },
          },
        };
      });

    return [{ points }];
  }

  private constructClusterPopupTableData(
    vehicle: VehicleDto,
    iconUrl: string
  ): { [header: string]: string } {
    const popupData: { [header: string]: string } = {};
    popupData[this.translate.instant('General.Icon')] = iconUrl;
    popupData[this.translate.instant('VehicleDriverPopup.Description')] =
      vehicle.VehicleDescription || '';
    popupData[this.translate.instant('VehicleDriverPopup.DefaultDriver')] =
      vehicle.Username || '';
    popupData[this.translate.instant('VehicleDriverPopup.Mobile')] =
      vehicle.UserMobile || '';
    popupData[this.translate.instant('VehicleDriverPopup.LastActivity')] =
      vehicle.LastActivityTime
        ? DateUtils.convertDateTimeToNO(new Date(vehicle.LastActivityTime))
        : '';
    return popupData;
  }

  private getTooltipFromMapSettings(
    vehicle: VehicleDto,
    mapSettings?: CustomVehicleMapSettings
  ): string | undefined {
    let tooltip = '';

    if (mapSettings?.RegistrationNumber) {
      tooltip += vehicle.RegistrationNumber || '';
    }

    if (mapSettings?.UserDescription && vehicle.UserDescription) {
      tooltip +=
        tooltip !== ''
          ? '<br>' + vehicle.UserDescription
          : vehicle.UserDescription;
    }

    if (mapSettings?.VehicleDescription && vehicle.VehicleDescription) {
      tooltip +=
        tooltip !== ''
          ? '<br>' + vehicle.VehicleDescription
          : vehicle.VehicleDescription;
    }

    if (mapSettings?.Comment && vehicle.Comment) {
      tooltip += tooltip !== '' ? '<br>' + vehicle.Comment : vehicle.Comment;
    }

    return tooltip !== '' ? tooltip : undefined;
  }
}
